import { ChevronLeft, ChevronRight, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import RecentArticles from '@/components/sections/RecentArticles'
import Newsletter from '@/components/sections/Newsletter'

interface StrategicPillar {
  id: string
  title: string
  image: string
  href: string
}

const NavigationPositioning = () => {
  const otherPillars: StrategicPillar[] = [
    {
      id: 'earth-observation',
      title: 'Earth Observation',
      image: 'https://res.cloudinary.com/dlwegkhme/image/upload/v1755760297/333506ab901b83e4fee5ac8a037c9429a71fb1f8_1_fgkekh.png',
      href: '/programmes/earth-observation'
    },
    {
      id: 'satellite-connectivity',
      title: 'Satellite Connectivity',
      image: 'https://res.cloudinary.com/dlwegkhme/image/upload/v1755760295/58f94f6e2b13884d42b79e08cb35f2f7792f1574_1_y7ilff.png',
      href: '/programmes/satellite-connectivity'
    },
    {
      id: 'astronomy',
      title: 'Astronomy',
      image: 'https://res.cloudinary.com/dlwegkhme/image/upload/v1755760294/08e9490cafa8ca7c9af8f4b5f2cd143847d997ac_1_huxntd.png',
      href: '/programmes/astronomy'
    }
  ]

  const tags = ['Navigation Systems', 'GPS Technology', 'Transportation', 'Positioning Services']

  const handleShare = (platform: string) => {
    const url = window.location.href
    const title = 'Navigation & Positioning - African Space Agency'

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank')
        break
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank')
        break
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank')
        break
      case 'instagram':
        // Instagram doesn't support direct URL sharing
        navigator.clipboard.writeText(url)
        alert('Link copied to clipboard! You can share this on Instagram.')
        break
      default:
        navigator.clipboard.writeText(url)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Strategic Pillars', href: '/programmes' },
    { label: 'Navigation & Positioning', href: '/programmes/navigation-positioning', active: true }
  ]

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <div className=" mb-5">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Main Content Container */}
      <div className="space-y-10">
        {/* Hero Section */}
        <div className="space-y-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900 leading-tight lg:leading-12">
            Navigation & Positioning
          </h1>

          <div className="space-y-2">
            <img
              src="https://res.cloudinary.com/dlwegkhme/image/upload/v1755760295/841cca148322e24bfe3c2a99bf215d50038974e2_1_qkytre.png"
              alt="Navigation and positioning systems technology visualization"
              className="w-full h-64 sm:h-80 md:h-96 lg:h-[502px] object-cover rounded"
            />
            <p className="text-base font-medium font-satoshi text-gray-600">
              Developing indigenous navigation systems and precision positioning technologies for Africa.
            </p>
          </div>
        </div>

        {/* Content and Sidebar Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 order-1">
            <div className="space-y-10">
              {/* Article Content */}
              <div className="space-y-6">
                <div className="space-y-6">
                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    Space infrastructure is proving increasingly valuable in addressing societal challenges. Whether it's providing critical data for disaster prevention and management, accurate weather updates, and precise monitoring for environmental pollution or climate data research, satellite navigation and positioning applications are pivotal to surviving Earth current climates and ensuring human safety.
                  </p>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    Intelligent transportation systems utilize navigation technology to enhance road, air, and sea travel safety, reduce fuel consumption, and optimize routes, benefiting both travelers and businesses, especially for cross-border fleet management.
                  </p>

                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    As a continent focused on socioeconomic development and enhanced regional and global business activities, space technology continues to bring us closer and closer to our developmental goals. Navigation and positioning systems are essential for modern logistics, agriculture, and emergency response services across Africa.
                  </p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-4">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-6 py-1.5 bg-gray-50 rounded-full text-base font-medium font-satoshi text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Social Sharing */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <span className="text-lg font-normal font-pt-serif text-gray-600">
                  SHARE
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="w-full h-full" />
                  </button>
                  <button
                    onClick={() => handleShare('instagram')}
                    className="w-6 h-6 text-gray-800 hover:text-pink-600 transition-colors"
                    aria-label="Share on Instagram"
                  >
                    <Instagram className="w-full h-full" />
                  </button>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <button className="w-full sm:w-auto px-6 py-3 bg-white rounded-lg border border-gray-300 flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-base font-medium font-satoshi text-gray-900">
                    Previous
                  </span>
                </button>

                <button className="w-full sm:w-auto px-6 py-3 bg-white rounded-lg border border-gray-300 flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors">
                  <span className="text-base font-medium font-satoshi text-gray-900">
                    Next
                  </span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 order-2">
            <div className="space-y-10">
              {/* Other Strategic Pillars */}
              <div className="space-y-6">
                <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                  OTHER STRATEGIC PILLARS
                </h3>

                <div className="space-y-8">
                  {otherPillars.map((pillar) => (
                    <a
                      key={pillar.id}
                      href={pillar.href}
                      className="group block"
                    >
                      <article className="space-y-4">
                        <div className="w-full h-72">
                          <img
                            src={pillar.image}
                            alt={pillar.title}
                            className="w-full h-full object-cover rounded group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <h4 className="text-lg font-normal font-pt-serif text-gray-900 leading-relaxed group-hover:text-wine-100 transition-colors">
                          {pillar.title}
                        </h4>
                      </article>
                    </a>
                  ))}
                </div>
              </div>

              {/* Related Articles */}
              <RecentArticles />

              {/* Newsletter */}
              <Newsletter />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NavigationPositioning